import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, ArrowRight, Sparkles } from "lucide-react";

interface LandingHeroProps {
  badge?: string;
  title: React.ReactNode;
  subtitle: string;
  showUrlInput?: boolean;
  onAnalyze?: (url: string) => void;
  ctaLabel?: string;
  trustItems?: { icon: React.ElementType; text: string }[];
}

const LandingHero = ({
  badge,
  title,
  subtitle,
  showUrlInput = false,
  onAnalyze,
  ctaLabel = "Analyze My Site",
  trustItems,
}: LandingHeroProps) => {
  const [url, setUrl] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    onAnalyze?.(url.trim());
  };

  return (
    <header className="relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(160_84%_45%/0.08),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,hsl(265_85%_60%/0.06),transparent_50%)]" />

      <div className="relative z-10 container mx-auto px-6 pt-20 pb-24 text-center max-w-4xl">
        {badge && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-secondary/50 text-sm text-muted-foreground mb-8">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            {badge}
          </div>
        )}

        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
          {title}
        </h1>

        <p className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
          {subtitle}
        </p>

        {showUrlInput && (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <div className="relative flex-1">
              <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-14 pl-11 pr-4 bg-secondary/50 border-border text-base rounded-xl focus:ring-primary"
                required
                aria-label="Enter your website URL for a free SEO analysis"
              />
            </div>
            <Button type="submit" size="lg" className="h-14 px-8 rounded-xl text-base font-semibold glow-primary">
              {ctaLabel}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </form>
        )}

        {trustItems && (
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            {trustItems.map((t) => (
              <div key={t.text} className="flex items-center gap-2 text-sm text-muted-foreground">
                <t.icon className="w-4 h-4 text-primary" />
                {t.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default LandingHero;
